import booleanValid from '@turf/boolean-valid';
import rectangleGrid from '@turf/rectangle-grid';
import {
  area,
  bbox,
  center,
  feature,
  Id,
  LineString,
  Point,
  Polygon,
  Position,
  Properties,
} from '@turf/turf';
import * as turf from '@turf/turf';
import {
  DEFAULT_SHARE_CAMERA_PIXEL_OFFSETS,
  FeatureTypes,
  MAP_MAX_ZOOM_LEVEL,
  MAP_MIN_ZOOM_LEVEL,
  TILE_SIZE_METERS_AT_0_ZOOM,
} from 'constants/map';
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from 'geojson';
import {
  ClientAttributeEntity,
  ICameraOffsets,
  IProjectile,
  ISelectOption,
} from 'interfaces';
import mapboxgl, { LngLatLike, Map } from 'mapbox-gl';
import { GeoJSONFeature, LngLat } from 'mapbox-gl';
import { CameraBounds, MapObject, TGeometry, TPosition } from 'types';
import {
  BoundaryGrid,
  CustomGeoJSONFeature,
  Entity,
  GeoJSONCompletedFeatureProperties,
  GeometryParameter,
  MapEntity,
  MapEntityTypes,
  MapFeatureLine,
  MapFeaturePoint,
  MapFeaturePolygon,
  MapFeatureProperties,
  NumberParameter,
  ParametersIdTitleMap,
  TextParameter,
} from 'types';

import { copyToClipboard, downloadFile, formatDate, notify } from 'utils';

import { mapEntityParams } from '../constants/entities';

import { getEntityParameterValue } from './entity';
import { constructIdentifier } from './entity';

interface GetFeaturesFromMapEntitiesReturnType {
  points: Record<string, FeatureCollection<Point, MapFeatureProperties>>;
  lines: FeatureCollection<LineString, MapFeatureProperties>;
  polygons: FeatureCollection<Polygon, MapFeatureProperties>;
}

const getFeaturesFromMapEntitiesInitial: GetFeaturesFromMapEntitiesReturnType =
  {
    points: {},
    lines: { type: 'FeatureCollection', features: [] },
    polygons: { type: 'FeatureCollection', features: [] },
  };

export const prepareFeatureCollection = (
  features?: Feature[]
): FeatureCollection => ({
  type: 'FeatureCollection',
  features: features ?? [],
});

export const preparePolygonFeature = <T = any>(
  id: string | number | undefined,
  coordinates: Position[][],
  properties: T
): Feature<Polygon, T> => ({
  type: 'Feature',
  id,
  geometry: { type: 'Polygon', coordinates: coordinates },
  properties,
});

export const getCoordinatesCenter = (coordinates: TPosition[]): TPosition => {
  const sum = coordinates.reduce(
    (acc, coordinate) => [acc[0] + coordinate[0], acc[1] + coordinate[1]],
    [0, 0]
  );

  return [sum[0] / coordinates.length, sum[1] / coordinates.length];
};

export const roundCoordinates = (
  coordinates: TPosition | TPosition[]
): TPosition | TPosition[] => {
  if (typeof coordinates[0] === 'number') {
    const [lat, lon] = coordinates as TPosition;
    return [parseFloat(lat.toFixed(6)), parseFloat(lon.toFixed(6))];
  } else {
    return (coordinates as TPosition[]).map(
      (position) => roundCoordinates(position) as TPosition
    );
  }
};

export const getZoomPercent = (
  zoom: number,
  minZoom = MAP_MIN_ZOOM_LEVEL,
  maxZoom = MAP_MAX_ZOOM_LEVEL
) => ((zoom - minZoom) / (maxZoom - minZoom)) * 100;

export const getMetersPerPixel = (latitude: number, zoomLevel: number) => {
  const EARTH_RADIUS = 6378137;
  const TILESIZE = 256;
  const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS;

  const scale = Math.pow(2, zoomLevel);
  const worldSize = TILESIZE * scale;
  const latitudeRadians = latitude * (Math.PI / 180);

  return (EARTH_CIRCUMFERENCE * Math.cos(latitudeRadians)) / worldSize;
};

export const getPixelValueByMeters = (
  meters: number,
  latitude: number,
  zoom: number
) => meters / getMetersPerPixel(latitude, zoom);

export const downloadObject = (
  object: string,
  name: string,
  fileOptions?: BlobPropertyBag
) => {
  try {
    const blob = new Blob([object], fileOptions);
    const blobUrl = URL.createObjectURL(blob);

    downloadFile(blobUrl, name);
  } catch {
    notify.error('Не удалось скопировать файл');
  }
};

export const getAttributeOptionsFromClient = (
  attributes: ClientAttributeEntity[]
): ISelectOption[] =>
  attributes.map(({ name, id, code }) => ({
    label: code === null ? name : code + ' ' + name,
    value: id,
  }));

export const getRadiusFromString = (radius: string) => {
  const regex = /^(\d+(\.\d+)?)-(\d+(\.\d+)?) км$/;
  const match = radius.match(regex);
  if (match) {
    const min_range = parseFloat(match[1]);
    const max_range = parseFloat(match[3]);

    return [min_range, max_range];
  }
  throw new Error('Invalid input format');
};

export const getListOfRangeInMeters = (projectiles: IProjectile[]) =>
  projectiles.map((el) => 1000 * getRadiusFromString(el.range)[1]);

export const getMapObject = (
  entity: MapEntity,
  parametersIdTitleMap: ParametersIdTitleMap
): MapObject => {
  const geometry = getEntityParameterValue<GeometryParameter>(
    entity.entity,
    parametersIdTitleMap,
    mapEntityParams.GEOMETRY
  ) ?? {
    type: 'Point',
    coordinates: [0, 0],
  };

  const type = getEntityParameterValue<TextParameter>(
    entity.entity,
    parametersIdTitleMap,
    mapEntityParams.TYPE
  );

  const status = getEntityParameterValue<TextParameter>(
    entity.entity,
    parametersIdTitleMap,
    mapEntityParams.STATUS
  );

  const color = getEntityParameterValue<TextParameter>(
    entity.entity,
    parametersIdTitleMap,
    mapEntityParams.COLOR
  );

  const opacity = getEntityParameterValue<NumberParameter>(
    entity.entity,
    parametersIdTitleMap,
    mapEntityParams.OPACITY
  );

  return {
    id: String(entity.entity.id),
    name: entity.entity.title,
    geometry: geometry,
    type: type,
    status: status,
    color: color,
    opacity: opacity,
  };
};

export const createFeature = <
  G extends TGeometry = TGeometry,
  P extends Properties = Properties
>(
  geometry: G,
  properties: P,
  id?: Id
) => feature(geometry, properties, { id });

export const isFeatureValid = (feature: Feature<any>) => booleanValid(feature);

export const calculateFeatureCenter = (feature: Feature<any>): Position =>
  center(feature).geometry.coordinates;

export const calculateGeometryCenter = (geometry: TGeometry): Position => {
  const feature = createFeature(geometry, null);

  return center(feature).geometry.coordinates;
};

export const getFeaturesFromMapEntities = (
  entities: MapEntity[],
  parametersIdTitleMap: ParametersIdTitleMap,
  showObjectIDs: boolean
) =>
  entities.reduce((acc, curr) => {
    if (curr.info?.type !== MapEntityTypes.OBJECT || !curr.state.active) {
      return acc;
    }

    const mapObject = getMapObject(curr, parametersIdTitleMap);
    const title = showObjectIDs
      ? `${constructIdentifier(mapObject.id)}\n${mapObject.name}`
      : mapObject.name;

    const mapFeature = createFeature(
      mapObject.geometry,
      {
        title,
        width: 5,
        type: mapObject.type,
        status: mapObject.status,
        lineColor: mapObject.color,
        fillColor: mapObject.color,
        opacity: getProperOpacity(mapObject, 0.7),
      },
      mapObject.id
    );

    if (mapFeature.geometry.type === 'Point') {
      const type = 'FeatureCollection' as const;
      const mapObjectType = mapObject.type ?? 'Неизвестный тип';

      return {
        ...acc,
        points: {
          ...acc.points,
          [mapObjectType]: {
            type: type,
            features: [
              ...(acc.points[mapObjectType]?.features ?? []),
              mapFeature as MapFeaturePoint,
            ],
          },
        },
      };
    }

    if (mapFeature.geometry.type === 'LineString') {
      return {
        ...acc,
        lines: {
          ...acc.lines,
          features: [...acc.lines.features, mapFeature as MapFeatureLine],
        },
      };
    }

    if (mapFeature.geometry.type === 'Polygon') {
      return {
        ...acc,
        polygons: {
          ...acc.polygons,
          features: [...acc.polygons.features, mapFeature as MapFeaturePolygon],
        },
      };
    }

    return acc;
  }, getFeaturesFromMapEntitiesInitial);

export const getInitialMapObject = (
  id = 0,
  mapObjectTemplateID: number,
  paramIdMap: Record<mapEntityParams, string>,
  geometry: GeoJSON.Geometry,
  parentEntityID?: number
): Entity => ({
  id: id,
  parentEntityID,
  templateID: mapObjectTemplateID,
  title: '',
  parameters: {
    [paramIdMap[mapEntityParams.GEOMETRY]]: { value: geometry },
    [paramIdMap[mapEntityParams.OPACITY]]: { value: 25 },
    [paramIdMap[mapEntityParams.DESCRIPTION]]: { value: '' },
    [paramIdMap[mapEntityParams.DATE]]: { value: formatDate(new Date()) },
    [paramIdMap[mapEntityParams.COLOR]]: { value: '#2196f3' },
    [paramIdMap[mapEntityParams.TYPE]]: { value: 'Другое' },
    [paramIdMap[mapEntityParams.STATUS]]: { value: 'Вскрыто' },
    [paramIdMap[mapEntityParams.MEDIA]]: { value: [] },
    [paramIdMap[mapEntityParams.RELEVANCE]]: { value: 'Актуально' },
    [paramIdMap[mapEntityParams.RELIABILITY]]: { value: 'Почти наверняка' },
  },
});

export const getProperOpacity = (
  item: {
    opacity?: number | null;
    [key: string]: any;
  },
  defaultOpacity = 1
) =>
  item.opacity === undefined || item.opacity === null
    ? defaultOpacity
    : item.opacity / 100;

export const getRectangleGrid = <T extends GeoJsonProperties = any>(
  feature: GeoJSONFeature,
  size: number,
  properties?: T
) => {
  const featureBbox = bbox(feature);
  const [west, south, east, north] = featureBbox;
  const width = (east - west) / size;
  const height = (north - south) / size;

  const grid = rectangleGrid<T>(featureBbox, width, height, {
    units: 'degrees',
    properties,
  });

  return grid;
};

export const getBoundaryGrid = (
  feature: GeoJSONFeature,
  size: number,
  properties?: GeoJsonProperties
) => {
  const boundaryGrid = getRectangleGrid<GeoJsonProperties>(
    feature,
    size,
    properties
  ) as BoundaryGrid;

  const featureId = feature.id ?? feature.properties?.id;

  boundaryGrid.relatedFeatureId = featureId;
  boundaryGrid.visible = true;
  boundaryGrid.showLabels = false;
  boundaryGrid.features.forEach((boundaryCell, index) => {
    const column = size - (index % size);
    const row = Math.floor(index / size) + 1;
    const label = `${column}${row}`;
    const id = `${featureId}-${index}-${label}`;
    const sq = area(feature);

    boundaryCell.id = id;
    boundaryCell.properties = {
      ...properties,
      id: id,
      relatedFeatureId: featureId,
      index: index,
      size: size,
      area: sq,
      label: label,
      showLabel: false,
      filled: false,
    };
  });

  return boundaryGrid;
};

export const getGeoJSONCompletedFeature = <
  G extends Geometry = any,
  P extends GeoJSONCompletedFeatureProperties = any
>(
  feature: CustomGeoJSONFeature<G, P>
) => {
  const clonedFeature = feature.clone();
  const { geometry, ...restProperties } = feature.properties;

  clonedFeature.geometry = JSON.parse(geometry);
  clonedFeature.properties = restProperties;

  return clonedFeature as CustomGeoJSONFeature<G, Omit<P, 'geometry'>>;
};

export const getQueriedFeaturesBounds = (
  position: { x: number; y: number },
  offset = 3
): [[number, number], [number, number]] => [
  [position.x - offset, position.y - offset],
  [position.x + offset, position.y + offset],
];

export const lngLatFromCoords = (coordinates: TPosition) => {
  return new LngLat(coordinates[0], coordinates[1]);
};

export const copyShareLinkToBuffer = (
  entityId: string | number,
  subdomain = 'map'
) => {
  const constructLink = (id: string | number) =>
    `${window.location.origin}/${subdomain}/${id}`;
  const link = constructLink(entityId);
  copyToClipboard(link, 'Ссылка скопирована в буфер обмена');
};

export const getEntityCoordinates = (
  geometry: GeometryParameter | null
): TPosition => {
  if (!geometry || geometry.coordinates.length == 0) {
    return [0, 0];
  }

  switch (geometry.type) {
    case FeatureTypes.POINT:
      return geometry.coordinates as TPosition;
    case FeatureTypes.LINE:
      return getCoordinatesCenter(geometry.coordinates as TPosition[]);
    case FeatureTypes.POLYGON:
      return getCoordinatesCenter(geometry.coordinates[0] as TPosition[]);
    default:
      return [0, 0];
  }
};

export const getResolutionFromZoomLevel = (
  zoom: number,
  latitude: number
): number =>
  (TILE_SIZE_METERS_AT_0_ZOOM * Math.cos((latitude * Math.PI) / 180)) /
  Math.pow(2, zoom + 1);

export const getMeterOffsets = (
  resolution: number,
  pixelOffsets: ICameraOffsets = DEFAULT_SHARE_CAMERA_PIXEL_OFFSETS
): ICameraOffsets => ({
  up: pixelOffsets.up * resolution,
  down: pixelOffsets.down * resolution,
  right: pixelOffsets.right * resolution,
  left: pixelOffsets.left * resolution,
});

const movePoint = (initialPoint: Position, offset: number, bearing: number) =>
  turf.destination(initialPoint, offset, bearing, {
    units: 'meters',
  }).geometry.coordinates;

export const getAdjustedBounds = (
  initialBounds: CameraBounds,
  mapRef: Map | null,
  pixelOffsets: ICameraOffsets = DEFAULT_SHARE_CAMERA_PIXEL_OFFSETS
) => {
  const camera = mapRef?.cameraForBounds(initialBounds, {
    padding: pixelOffsets,
  });
  const cameraLngLat = mapboxgl.LngLat.convert(camera?.center || [0, 0]);
  const cameraZoom = camera?.zoom || 0;

  const paddedResolution = getResolutionFromZoomLevel(
    cameraZoom,
    cameraLngLat.lat
  );

  const meterOffsets = getMeterOffsets(paddedResolution, pixelOffsets);

  const initialSouthWest = [initialBounds[0], initialBounds[1]];
  const intermediateSouthWest = movePoint(
    initialSouthWest,
    meterOffsets.left,
    -90
  );
  const adjustedSouthWest = movePoint(
    intermediateSouthWest,
    meterOffsets.down,
    180
  );

  const initialNorthEast = [initialBounds[2], initialBounds[3]];
  const intermediateNorthEast = movePoint(
    initialNorthEast,
    meterOffsets.right,
    90
  );
  const adjustedNorthEast = movePoint(
    intermediateNorthEast,
    meterOffsets.up,
    0
  );

  const adjustedBounds = new mapboxgl.LngLatBounds();

  adjustedBounds.setNorthEast(
    adjustedNorthEast.length === 2
      ? [adjustedNorthEast[0], adjustedNorthEast[1]]
      : [0, 0]
  );
  adjustedBounds.setSouthWest(
    adjustedSouthWest.length === 2
      ? [adjustedSouthWest[0], adjustedSouthWest[1]]
      : [0, 0]
  );

  return adjustedBounds;
};

export const getAdjustedBoundsFromLngLatLike = (
  coordinates: LngLatLike[],
  mapRef: Map | null
) => {
  const bounds = new mapboxgl.LngLatBounds();
  coordinates.forEach((p) => bounds.extend(p));
  return getAdjustedBounds(
    [bounds.getSouth(), bounds.getWest(), bounds.getNorth(), bounds.getEast()],
    mapRef
  );
};
